import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, FileText, Upload, ShieldCheck, Users as UsersIcon, ClipboardCheck, FileWarning, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

// Phase B2 — formatDate is now sourced from useLocale() inside each tab
// component (RamsTab/InsuranceTab/PermitsTab). The legacy module-level
// fmtDate was removed to prevent accidental en-US fall-through (the old
// `new Date(iso).toLocaleDate...()` honoured browser locale, not org
// locale — exactly what B2 fixes platform-wide).
function daysFromNow(iso) {
  if (!iso) return null;
  return Math.round((new Date(iso) - new Date()) / 86400000);
}

function RamsTab({ companyId }) {
  const { formatDate: fmtDate } = useLocale();
  const [versions, setVersions] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkLink, setBulkLink] = useState(null);
  const inputRef = useRef(null);

  const refresh = async () => {
    try {
      const [v, a] = await Promise.all([
        api.get(`/contractor-companies/${companyId}/rams`),
        api.get(`/contractor-companies/${companyId}/rams/active`),
      ]);
      setVersions(v.data || []);
      setActive(a.data || null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { refresh(); }, [companyId]);

  const onUpload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("PDF only");
    if (file.size > 10 * 1024 * 1024) return toast.error("Max 10MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      const { data } = await api.post(`/contractor-companies/${companyId}/rams`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`RAMS v${data.version} uploaded`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const issueBulkLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contractor-companies/${companyId}/bulk-rams-link`);
      setBulkLink(data);
      toast.success("Bulk-RAMS link issued — copy/share it with your contractor team");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="rams-tab">
      <div
        className="rounded-2xl border-2 border-dashed border-navy-200 hover:border-accent transition-colors p-6 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          onUpload(f);
        }}
      >
        <Upload className="w-8 h-8 mx-auto text-navy-300 mb-2" />
        <div className="text-sm text-navy-700 font-medium">Drag a PDF or click to upload a new RAMS version</div>
        <div className="text-xs text-navy-500 mt-1">≤ 10MB · application/pdf only</div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
          data-testid="rams-file-input"
        />
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="rams-upload-btn"
        >
          {busy ? "Uploading…" : "Choose file"}
        </Button>
      </div>

      {/* Phase 5 P1 — Bulk RAMS acceptance link */}
      {active && (
        <div className="rounded-2xl bg-white border border-accent/40 p-5" data-testid="bulk-rams-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-navy-900 inline-flex items-center gap-2">
                <FileText className="w-4 h-4 text-accent" /> Bulk-RAMS acceptance link
              </div>
              <p className="text-xs text-navy-500 mt-1">
                Share with the contractor company's primary contact so every team-member can
                accept v{active.version} of the active RAMS in one go (link expires in 14 days).
              </p>
            </div>
            <Button onClick={issueBulkLink} disabled={busy}
                    className="bg-accent hover:bg-accent/90 text-white"
                    data-testid="issue-bulk-rams-btn">
              {busy ? "Issuing…" : "Issue bulk link"}
            </Button>
          </div>
          {bulkLink && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-navy-50 border border-navy-100 flex items-center gap-2">
              <code className="text-xs text-navy-700 truncate flex-1" data-testid="bulk-rams-url">
                {bulkLink.url}
              </code>
              <Button size="sm" variant="ghost" onClick={() => {
                navigator.clipboard.writeText(bulkLink.url);
                toast.success("Copied");
              }} data-testid="bulk-rams-copy">Copy</Button>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl bg-white border border-navy-100">
        <div className="px-5 py-3 border-b border-navy-100 font-semibold text-navy-800 flex items-center gap-2">
          <FileText className="w-4 h-4 text-accent" /> Versions
        </div>
        {versions.length === 0 ? (
          <div className="p-5 text-sm text-navy-500">No RAMS uploaded yet.</div>
        ) : (
          <ul className="divide-y divide-navy-100" data-testid="rams-versions-list">
            {versions.map((v) => (
              <li key={v.id} className="px-5 py-3 flex items-center gap-3">
                <span className="font-mono text-xs px-2 py-0.5 rounded bg-navy-50 text-navy-700">v{v.version}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-navy-900 truncate">{v.title || `RAMS v${v.version}`}</div>
                  <div className="text-xs text-navy-500">Uploaded {fmtDate(v.uploaded_at)}</div>
                </div>
                {v.active && (
                  <span className="text-xs font-semibold text-success-700 bg-success-50 px-2 py-0.5 rounded-full">
                    ACTIVE
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function InsuranceTab({ companyId }) {
  const { formatDate: fmtDate } = useLocale();
  const [certs, setCerts] = useState([]);
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [type, setType] = useState("PublicLiability");

  const refresh = async () => {
    try {
      const { data } = await api.get(`/contractor-companies/${companyId}/insurance`);
      setCerts(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { refresh(); }, [companyId]);

  const onUpload = async (file) => {
    if (!file) return;
    if (!expiresAt) return toast.error("Set expires_at first");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("certificate_type", type);
      fd.append("expires_at", new Date(expiresAt).toISOString());
      await api.post(`/contractor-companies/${companyId}/insurance`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Insurance uploaded");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Archive this certificate?")) return;
    try {
      await api.delete(`/insurance-certificates/${id}`);
      toast.success("Archived");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="insurance-tab">
      <div className="rounded-2xl border border-navy-100 bg-white p-5 flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1">
          <label className="text-xs font-medium text-navy-600">Type</label>
          <select className="w-full mt-1 px-3 py-2 border border-navy-200 rounded-lg" value={type} onChange={(e) => setType(e.target.value)}>
            <option>PublicLiability</option>
            <option>EmployersLiability</option>
            <option>ProfessionalIndemnity</option>
            <option>Other</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="text-xs font-medium text-navy-600">Expires at</label>
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-navy-200 rounded-lg"
            data-testid="insurance-expires-at"
          />
        </div>
        <Button onClick={() => inputRef.current?.click()} disabled={busy} className="bg-accent hover:bg-accent/90 text-white" data-testid="insurance-upload-btn">
          {busy ? "Uploading…" : "Upload certificate"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
          data-testid="insurance-file-input"
        />
      </div>

      <div className="rounded-2xl bg-white border border-navy-100">
        <div className="px-5 py-3 border-b border-navy-100 font-semibold text-navy-800 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-accent" /> Certificates
        </div>
        {certs.length === 0 ? (
          <div className="p-5 text-sm text-navy-500">No certificates uploaded.</div>
        ) : (
          <ul className="divide-y divide-navy-100">
            {certs.map((c) => {
              const d = daysFromNow(c.expires_at);
              const tone = d == null ? "muted" : d < 0 ? "danger" : d <= 7 ? "danger" : d <= 30 ? "warn" : "ok";
              return (
                <li key={c.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-navy-900">{c.certificate_type}</div>
                    <div className="text-xs text-navy-500">Expires {fmtDate(c.expires_at)} · {c.policy_number || "no policy #"}</div>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      tone === "ok" ? "bg-success-50 text-success-700"
                      : tone === "warn" ? "bg-warning-50 text-warning-700"
                      : tone === "danger" ? "bg-danger-50 text-danger-700"
                      : "bg-navy-50 text-navy-600"
                    }`}
                  >
                    {d == null ? "—" : d < 0 ? `${-d}d expired` : `${d}d`}
                  </span>
                  {c.active && (
                    <Button variant="ghost" size="icon" onClick={() => onDelete(c.id)} data-testid={`del-cert-${c.id}`}>
                      <Trash2 className="w-4 h-4 text-danger-500" />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function ContractorsTab({ companyId }) {
  const [contractors, setContractors] = useState([]);
  const [name, setName] = useState("");

  const refresh = async () => {
    try {
      const { data } = await api.get("/contractors", { params: { company_id: companyId } });
      const list = data?.items ?? data ?? [];
      setContractors(list.filter(c => (c.contractor_company_id || c.company_id) === companyId));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { refresh(); }, [companyId]);

  const onAdd = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/contractors", { contractor_company_id: companyId, name: name.trim() });
      setName("");
      toast.success("Contractor added");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onMarkInducted = async (id) => {
    try {
      await api.post(`/contractors/${id}/mark-inducted`);
      toast.success("Induction recorded (1y validity)");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="contractors-tab">
      <div className="rounded-2xl border border-navy-100 bg-white p-5 flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Add contractor full name"
          className="flex-1 px-3 py-2 border border-navy-200 rounded-lg"
          data-testid="add-contractor-name"
        />
        <Button onClick={onAdd} className="bg-accent hover:bg-accent/90 text-white" data-testid="add-contractor-btn">Add</Button>
      </div>
      {contractors.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-navy-200 p-8 text-center text-navy-500">No contractors yet.</div>
      ) : (
        <ul className="rounded-2xl bg-white border border-navy-100 divide-y divide-navy-100">
          {contractors.map((c) => {
            const induction = c.induction_complete ? (daysFromNow(c.induction_expiry) || 0) : null;
            return (
              <li key={c.id} className="px-5 py-3 flex items-center gap-3" data-testid={`contractor-row-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-navy-900">{c.name}</div>
                  <div className="text-xs text-navy-500">{c.email || "no email"}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                  c.induction_complete && (induction == null || induction > 0)
                    ? "bg-success-50 text-success-700"
                    : "bg-danger-50 text-danger-700"
                }`}>
                  {c.induction_complete && (induction == null || induction > 0) ? "Inducted" : "Induction missing/expired"}
                </span>
                <Button variant="outline" size="sm" onClick={() => onMarkInducted(c.id)}>Mark inducted</Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function PermitsTab({ companyId }) {
  const { formatDate: fmtDate } = useLocale();
  const [permits, setPermits] = useState([]);
  useEffect(() => {
    api.get("/permits-to-work", { params: { contractor_company_id: companyId } })
       .then(({ data }) => setPermits((data?.items ?? data ?? []).filter(p => p.contractor_company_id === companyId)))
       .catch((e) => toast.error(formatApiError(e)));
  }, [companyId]);
  return (
    <div className="space-y-3" data-testid="permits-tab">
      <Link to="/permits-to-work" className="text-accent text-sm font-medium hover:underline inline-flex items-center gap-1">
        Manage permits in full <ExternalLink className="w-3.5 h-3.5" />
      </Link>
      {permits.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-navy-200 p-8 text-center text-navy-500">No permits.</div>
      ) : (
        <ul className="rounded-2xl bg-white border border-navy-100 divide-y divide-navy-100">
          {permits.map((p) => (
            <li key={p.id} className="px-5 py-3 flex items-center gap-3">
              <ClipboardCheck className="w-4 h-4 text-accent" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-navy-900">{p.permit_type}</div>
                <div className="text-xs text-navy-500">{fmtDate(p.valid_from)} → {fmtDate(p.valid_to)}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                p.status === "active" ? "bg-success-50 text-success-700" :
                p.status === "scheduled" ? "bg-navy-50 text-navy-600" :
                "bg-danger-50 text-danger-700"
              }`}>{p.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ContractorCompanyDetail() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/contractor-companies").then(({ data }) => {
      const list = data?.items ?? data ?? [];
      setCompany(list.find((c) => c.id === companyId));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [companyId]);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="contractor-company-detail">
        <div className="flex items-center gap-3">
          <Link to="/contractors" className="text-navy-500 hover:text-navy-900" data-testid="back-to-contractors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-navy-900 tracking-tight">{company?.name || "Loading…"}</h1>
        </div>
        <Tabs defaultValue="rams">
          <TabsList>
            <TabsTrigger value="rams" data-testid="tab-rams"><FileText className="w-4 h-4 mr-1.5" />RAMS</TabsTrigger>
            <TabsTrigger value="insurance" data-testid="tab-insurance"><ShieldCheck className="w-4 h-4 mr-1.5" />Insurance</TabsTrigger>
            <TabsTrigger value="contractors" data-testid="tab-contractors"><UsersIcon className="w-4 h-4 mr-1.5" />Contractors</TabsTrigger>
            <TabsTrigger value="permits" data-testid="tab-permits"><ClipboardCheck className="w-4 h-4 mr-1.5" />Permits</TabsTrigger>
          </TabsList>
          <TabsContent value="rams"><RamsTab companyId={companyId} /></TabsContent>
          <TabsContent value="insurance"><InsuranceTab companyId={companyId} /></TabsContent>
          <TabsContent value="contractors"><ContractorsTab companyId={companyId} /></TabsContent>
          <TabsContent value="permits"><PermitsTab companyId={companyId} /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
